import React from 'react'
import {
    Ruler, Scissors, AlertCircle, CheckCircle2, Info,
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from "@/lib/utils";

const SectionHeader = ({ icon: Icon, title, description }) => (
    <div className="space-y-3">
        <div className="flex items-center gap-3">
            <Icon
                className="h-8 w-8 text-blue-500 flex-shrink-0"
                strokeWidth={2}
            />
            <h2 className="text-[1.35rem] font-semibold text-gray-900 leading-tight">
                {title}
            </h2>
        </div>
        {description && (
            <p className="text-[0.95rem] text-gray-600 leading-relaxed ml-[2.75rem]">
                {description}
            </p>
        )}
    </div>
)

const InfoBox = ({ icon: Icon, title, content, color = 'blue', className = '' }) => (
    <div
        className={cn(
            "bg-white rounded-2xl border",
            `border-${color}-100/60`,
            "p-4 sm:p-5",
            "hover:shadow-lg transition-all duration-300",
            className
        )}
    >
        <div className="flex items-center gap-2.5 mb-4">
            <Icon className={`h-5 w-5 text-${color}-500`} strokeWidth={1.5} />
            <h4 className={`text-[1.05rem] font-medium text-${color}-900`}>
                {title}
            </h4>
        </div>
        {content}
    </div>
)

const ListItem = ({ icon: Icon, text, color = 'blue', isHighlighted = false }) => (
    <div
        className={cn(
            "flex items-start gap-2.5 p-2.5 rounded-lg transition-colors duration-200",
            isHighlighted ? `bg-${color}-50/70` : '',
            `hover:bg-${color}-50/80`
        )}
    >
        <Icon
            className={`h-4 w-4 mt-0.5 text-${color}-500 flex-shrink-0`}
            strokeWidth={1.5}
        />
        <p className="text-[0.95rem] text-gray-700 leading-relaxed">
            {text}
        </p>
    </div>
)

const ValueDisplay = ({ value, unit = '', className = '' }) => (
    <div className={cn(
        "font-medium text-gray-900",
        "text-lg sm:text-xl",
        className
    )}>
        {value}
        {unit && (
            <span className="text-gray-500 ml-1 text-[0.9em]">
                {unit}
            </span>
        )}
    </div>
)

const CaracteristicasFisicasCard = ({ caracteristicasFisicas }) => {
    if (!caracteristicasFisicas) return null
    const { tamano, variedades, puntosDelicados } = caracteristicasFisicas

    return (
        <Card className="w-full max-w-7xl mx-auto bg-gradient-to-br from-gray-50 to-white shadow-lg overflow-hidden">
            <CardContent className="p-4 sm:p-6 lg:p-8">
                <div className="space-y-6 sm:space-y-8">
                    <SectionHeader
                        icon={Ruler}
                        title="Características Físicas"
                        description="Guía detallada de estructura y cuidados especiales"
                    />

                    {/* Main content wrapper with max-width constraint */}
                    <div className="max-w-full">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
                            {/* Tamaño Section */}
                            <div className="w-full space-y-4">
                                <div className="flex items-center gap-2.5">
                                    <Ruler className="h-6 w-6 text-blue-500 flex-shrink-0" strokeWidth={1.5} />
                                    <h3 className="text-[1.25rem] font-semibold text-gray-800">
                                        Medidas y Peso
                                    </h3>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {tamano?.peso && (
                                        <InfoBox
                                            icon={Info}
                                            title="Peso Ideal"
                                            color="blue"
                                            className="w-full"
                                            content={
                                                <div className="space-y-3">
                                                    <ValueDisplay value={tamano.peso.rango} />
                                                    {tamano.peso.consejo && (
                                                        <ListItem
                                                            icon={AlertCircle}
                                                            text={tamano.peso.consejo}
                                                            color="blue"
                                                            isHighlighted
                                                        />
                                                    )}
                                                </div>
                                            }
                                        />
                                    )}
                                    {tamano?.altura && (
                                        <InfoBox
                                            icon={Info}
                                            title="Altura"
                                            color="blue"
                                            className="w-full"
                                            content={
                                                <div className="space-y-3">
                                                    <ValueDisplay value={tamano.altura.rango} />
                                                    {tamano.altura.importante && (
                                                        <ListItem
                                                            icon={CheckCircle2}
                                                            text={tamano.altura.importante}
                                                            color="blue"
                                                            isHighlighted
                                                        />
                                                    )}
                                                </div>
                                            }
                                        />
                                    )}
                                </div>
                            </div>

                            {/* Variedades Section */}
                            <div className="w-full space-y-4">
                                <div className="flex items-center gap-2.5">
                                    <Scissors className="h-6 w-6 text-emerald-500 flex-shrink-0" strokeWidth={1.5} />
                                    <h3 className="text-[1.25rem] font-semibold text-gray-800">
                                        Variedades y Tipos
                                    </h3>
                                </div>

                                {variedades?.pelo && (
                                    <InfoBox
                                        icon={Scissors}
                                        title="Tipos de Pelo"
                                        color="emerald"
                                        className="w-full"
                                        content={
                                            <div className="space-y-3">
                                                <div className="space-y-2">
                                                    {variedades.pelo.tipos?.map((tipo, idx) => (
                                                        <ListItem
                                                            key={idx}
                                                            icon={CheckCircle2}
                                                            text={tipo}
                                                            color="emerald"
                                                            isHighlighted={idx % 2 === 0}
                                                        />
                                                    ))}
                                                </div>

                                                {variedades.pelo.cuidados?.length > 0 && (
                                                    <div className="space-y-2 pt-3 mt-3 border-t border-emerald-100/60">
                                                        {variedades.pelo.cuidados?.map((cuidado, idx) => (
                                                            <ListItem
                                                                key={`care-${idx}`}
                                                                icon={AlertCircle}
                                                                text={cuidado}
                                                                color="emerald"
                                                            />
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        }
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

export default CaracteristicasFisicasCard